import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import ErrorList from './error-list';

const SubmitErrorHandler = ({ onSubmitError, reset }) => {
  const { submitCount, isValid, isSubmitting, errors, values, status } =
    useFormikContext();

  const [lastHandled, setLastHandled] = useState(0);
  const [errorsList, setErrorsList] = useState(null);

  // Only displays the generic "Enter your date of birth" message if no DOB parts were provided
  if (errors?.dob?._) {
    delete errors?.dob?.day;
    delete errors?.dob?.month;
    delete errors?.dob?.year;
  }

  useEffect(() => {
    if (reset) {
      setLastHandled(0);
      setErrorsList(null);
    }
  }, [reset]);

  // transform server-side errors from key: ["message"] to key: "message" (without array)
  const transformServerSideErrors = (errorObj) => {
    const transformedObj = {};
    const keys = Object.keys(errorObj);

    keys.forEach((key) => {
      const errorMessages = errorObj[key];
      const errorMessageString = errorMessages.join(' '); // join with space if there is more than one error message in the array
      transformedObj[key] = errorMessageString;
    });

    return transformedObj;
  };

  const statusErrors = useMemo(
    () => transformServerSideErrors(status?.errors || {}),
    [status]
  );

  useEffect(() => {
    if (submitCount > lastHandled && !isSubmitting && !isValid) {
      onSubmitError({ values, errors });
      setErrorsList(<ErrorList errors={errors} />);
      setLastHandled(submitCount);
    }

    // For clearing the errors when users fix them one by one, submitCount > 0 is to make sure that it's not first submission
    if (submitCount === lastHandled && submitCount > 0) {
      // onSubmitError not included to avoid jumping to another field while users are trying to fix the error in one field.
      setErrorsList(isEmpty(errors) ? null : <ErrorList errors={errors} />);
    }
  }, [
    submitCount,
    isValid,
    onSubmitError,
    lastHandled,
    errors,
    isSubmitting,
    values,
  ]);

  // Display server-side errors in ErrorList, errors are passed to FormikContext using setStatus()
  useEffect(() => {
    if (submitCount > lastHandled && !isSubmitting) {
      setErrorsList(
        isEmpty(statusErrors) ? null : <ErrorList errors={statusErrors} />
      );
      if (!isEmpty(statusErrors)) {
        onSubmitError({
          values,
          errors: statusErrors,
        });
      }
    }
  }, [
    statusErrors,
    values,
    onSubmitError,
    submitCount,
    lastHandled,
    isSubmitting,
  ]);

  return !isEmpty(errors) || !isEmpty(status) ? errorsList : null;
};

export default connect(SubmitErrorHandler);

SubmitErrorHandler.propTypes = {
  onSubmitError: PropTypes.func,
  reset: PropTypes.bool,
};
